import { googleMapsKey } from "./config.js";

// Google Maps wird erst geladen, wenn eine Seite es braucht (SOS-Panel).
// Der Schluessel kommt aus .env, nicht aus dem HTML.
let mapsLoading = null;

export function loadGoogleMaps() {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (mapsLoading) return mapsLoading;

    mapsLoading = new Promise((resolve, reject) => {
        if (!googleMapsKey) {
            reject(new Error("VITE_GOOGLE_MAPS_KEY fehlt"));
            return;
        }
        const script = document.createElement("script");
        script.src = "https://maps.googleapis.com/maps/api/js?key=" + encodeURIComponent(googleMapsKey) + "&loading=async";
        script.async = true;
        script.onload = () => resolve(window.google.maps);
        script.onerror = () => {
            mapsLoading = null; // naechster Aufruf versucht es erneut
            script.remove();
            reject(new Error("Google Maps konnte nicht geladen werden"));
        };
        document.head.appendChild(script);
    });
    return mapsLoading;
}

// Mit loading=async ist nach dem Script-Load nur der Loader da; die
// einzelnen Bibliotheken (Geocoder, Map) kommen erst per importLibrary.
// Wer new maps.Geocoder() direkt nach dem Laden aufruft, trifft je nach
// Timing ins Leere - daher hier immer ueber importLibrary.
function loadLibrary(name) {
    return loadGoogleMaps().then((maps) => maps.importLibrary(name));
}

/**
 * Aktuelle Position als Promise. Fehler tragen einen `reason`:
 * "unsupported" | "denied" | "unavailable" | "timeout".
 * Wird bei jedem Aufruf neu angefragt - eine Ablehnung wird von der App
 * nicht gemerkt; ob der Browser erneut fragt, entscheidet er selbst.
 */
export function getCurrentPosition(options = {}) {
    return new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
            reject(Object.assign(new Error("geolocation unsupported"), { reason: "unsupported" }));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            (err) => {
                const reason = err.code === 1 ? "denied" : err.code === 3 ? "timeout" : "unavailable";
                reject(Object.assign(new Error("geolocation: " + err.message), { reason, code: err.code }));
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0, ...options }
        );
    });
}

/** Stand der Standortfreigabe: "granted" | "prompt" | "denied" | "unknown". */
export function getGeolocationPermission() {
    if (!navigator.permissions?.query) return Promise.resolve("unknown");
    return navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => status.state)
        .catch(() => "unknown");
}

function geocode(lat, lng, attempt = 1) {
    return loadLibrary("geocoding")
        .then(({ Geocoder }) => new Geocoder().geocode({ location: { lat, lng } }))
        .then(({ results }) => {
            if (results && results.length > 0) return results;
            throw new Error("geocode: ZERO_RESULTS");
        })
        .catch((err) => {
            // Der Promise-Geocoder wirft bei jedem Nicht-OK-Status (UNKNOWN_ERROR,
            // OVER_QUERY_LIMIT ...). Einmal kurz warten und nochmals probieren.
            console.warn("Geocoding fehlgeschlagen (" + attempt + "):", err?.code || err?.message || err);
            if (attempt < 3 && String(err?.code || err?.message).indexOf("ZERO_RESULTS") === -1) {
                return new Promise((r) => setTimeout(r, 800 * attempt)).then(() => geocode(lat, lng, attempt + 1));
            }
            throw err;
        });
}

/** Adressbestandteil nach Typ aus einem Geocoder-Resultat. */
function pick(result, type, key = "long_name") {
    const c = result.address_components.find((comp) => comp.types.includes(type));
    return c ? c[key] : "";
}

/**
 * Position zu einer Adresse aufloesen. Nimmt die Bestandteile nach Typ
 * ueber alle Resultate hinweg (das erste Resultat ist das genaueste, hat
 * aber nicht immer jeden Bestandteil).
 * Liefert { street, number, zip, city, country, countryCode, formatted }.
 */
export function reverseGeocode(lat, lng) {
    return geocode(lat, lng).then((results) => {
        const first = (type, key) => {
            for (const r of results) {
                const v = pick(r, type, key);
                if (v) return v;
            }
            return "";
        };
        const address = {
            street: first("route"),
            number: first("street_number"),
            zip: first("postal_code"),
            city: first("locality") || first("postal_town") || first("administrative_area_level_2"),
            country: first("country"),
            countryCode: first("country", "short_name"),
            formatted: results[0].formatted_address || ""
        };
        const line = `${address.street} ${address.number}`.trim() + (address.zip || address.city ? `, ${address.zip} ${address.city}`.trim() : "");
        address.line = line.replace(/^, /, "") || address.formatted;
        return address;
    });
}

/** Adressteile [Strasse, Nr, PLZ, Ort, Land, Laenderkuerzel] zu Lat/Lng (Altschnittstelle). */
export function getAddress(lat, lng) {
    return reverseGeocode(lat, lng).then((a) => [a.street, a.number, a.zip, a.city, a.country, a.countryCode]);
}

/** Adresse als eine Zeile "Strasse Nr, PLZ Ort". */
export function getFormatedAddress(lat, lng) {
    return reverseGeocode(lat, lng).then((a) => a.line);
}

let map = null;
const mapStyle = [
    {
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "elementType": "labels.icon",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "elementType": "labels.text.stroke",
        "stylers": [
            {
                "color": "#f5f5f5"
            }
        ]
    },
    {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#bdbdbd"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "poi.park",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#ffffff"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#757575"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#dadada"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#616161"
            }
        ]
    },
    {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    },
    {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#e5e5e5"
            }
        ]
    },
    {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
            {
                "color": "#c9c9c9"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#9e9e9e"
            }
        ]
    }
];


// Idempotent: Haengt die Karte schon an genau diesem Element, wird sie nur
// neu zentriert und auf die aktuelle Groesse gebracht. Das braucht das
// SOS-Panel, weil Framework7 nach jedem $update() den Container neu
// rendern kann - dann steht die Karte auf einem verwaisten Element und
// bleibt leer. Der Aufrufer ruft initMap deshalb nach dem Rendern auf.
export async function initMap(mylat, mylng, mapElem) {
    const maps = await loadGoogleMaps();
    const { Map } = await loadLibrary("maps");
    const el = document.getElementById(mapElem);
    if (!el) return;

    if (map && map.getDiv() === el && el.childElementCount > 0) {
        maps.event.trigger(map, "resize");
        map.setCenter({ lat: mylat, lng: mylng });
        return;
    }

    map = new Map(el, {
        center: { lat: mylat, lng: mylng },
        zoom: 15,
        disableDefaultUI: true,
        styles: mapStyle
    });

    new maps.Marker({ position: { lat: mylat, lng: mylng }, map });
}
